/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { LocalizationWithComponent } from '@/components/blocks/LocalizationWithComponent';
import { NumberInput } from '@/components/blocks/NumberInput';
import { OneClick } from '@/components/blocks/OneClick';
import { OrderItemTableRowData } from '@/components/content/OrderItemTable/parts/Table';
import { orderItemTableQuantitySX } from '@/components/content/OrderItemTable/styles/orderItemTableQuantity';
import { productDetailsQuantitySX } from '@/components/content/ProductDetails/styles/quantity';
import { useCart } from '@/data/Content/Cart';
import { useOrderItemTableRow } from '@/data/Content/OrderItemTable';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { ContentContext } from '@/data/context/content';
import { EventsContext } from '@/data/context/events';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { FC, MouseEvent, useCallback, useContext, useMemo } from 'react';

export const OrderItemQuantity: FC<{ readOnly?: boolean; mini?: boolean }> = ({
	readOnly,
	mini = false,
}) => {
	const orderItemTableNLS = useLocalization('OrderItemTable');
	const localization = useLocalization('productDetail');
	const {
		quantity: { quantity, onChange },
		price,
		details,
		freeGift,
		page,
		details: {partNumber}
	} = useContext(ContentContext) as OrderItemTableRowData & ReturnType<typeof useOrderItemTableRow>
		& {page:string};
	const { addRemoveFreeItemToCart } = useContext(ContentContext) as ReturnType<typeof useCart>;
	const { onRemoveFromCart } = useContext(EventsContext);
	const { settings } = useSettings();
	const donateItemPartNumber = settings.userData.DonateItemPartNumber;

	const removeFromCart = async (event: MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		
		await onChange(0,"", details.name);

		onRemoveFromCart({
			gtm: {
				partNumber: details.partNumber,
				name: details.name,
				price: `${price.orderItemPrice}`,
				currency: price.currency,
				quantity: `${quantity}`,
				seller: details?.seller,
				sellerId: details?.sellerId,
				orgName: '', // TODO: specify selected org-name
				orgId: '', // TODO: specify selected org
				storeName: settings.storeName,
				settings,
			},
		});
	};

	const quantityChange = useCallback(
		async (updatedQuantity: number | null) => {
			if (updatedQuantity !== null && updatedQuantity >= 0 && updatedQuantity !== quantity) {
				onChange(updatedQuantity, details.maxOrderLimit, details.name);
			}
		},
		[onChange, quantity]
	);

	const debouncedQuantityChange = useMemo(
		() => debounce((updatedQuantity) => quantityChange(updatedQuantity), 800),
		[quantityChange]
	);
	
	const isDonationItem = useMemo(()=>
		!!donateItemPartNumber && donateItemPartNumber === `${partNumber}-000`,
	[partNumber, donateItemPartNumber]);

	return (
		<Stack alignItems="center" spacing={1} direction="row" sx={orderItemTableQuantitySX}>
			{readOnly ? (
				<>{mini ? (
					<LocalizationWithComponent
						text={localization.QuantityValue.t({ quantity })}
						components={[<Typography key="0" variant="body2" />, <Typography key="1" />]}
					/>
				) : (
					<Typography>{quantity}</Typography>
				)}
				{(['cart', 'minicart'].includes(page) && (freeGift || isDonationItem)) ? 
					<OneClick 
						wrapper="icon" 
						onClick={()=>
							addRemoveFreeItemToCart(
								details.partNumber,
								"REMOVE", 
								isDonationItem ? "DONATE_ITEM" : "FREE_GIFT"
							)
						} 
						spin={true} 
						spinSize={24}
					>
						<Box sx={{minWidth:'45px'}}>
							<DeleteOutlineIcon
								color="text"
								titleAccess={orderItemTableNLS.Labels.RemoveFromCart.t()}
							/>
						</Box>
					</OneClick>
				:null}
				</>
				
			) : (
				<>
					<NumberInput
						onChange={debouncedQuantityChange}
						value={quantity}
						min={0}
						sx={productDetailsQuantitySX}
						disabled={readOnly}
						showControls
						disallowEmptyOnBlur={true}
						data-testid="order-item-quantity"
						id="order-item-quantity"
						isControlled={true}
					/>
					<Box sx={{minWidth:'45px'}}>
						<OneClick wrapper="icon" onClick={removeFromCart} spin={true} spinSize={24}>						
							<DeleteOutlineIcon
								color="text"
								titleAccess={orderItemTableNLS.Labels.RemoveFromCart.t()}
							/> 
						</OneClick>
					</Box>
				</>
			)}
		</Stack>
	);
};
